import React from 'react';
import PropTypes from 'prop-types';
import { designSystem, Modal, PanelGroup, Panel } from '@yola/ws-ui';

const {
  ControlGroup,
  SearchableSelectField,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Box,
} = designSystem;

const MODAL_WIDTH = 334;
const FLOATING_ELEMENT_SELECTOR =
  '.ws-ui-base-select-option, .ws-ui-base-select__option, .ws-ui-base-select-menu';

const FolderOption = ({ value: folderId = null, label = '', foldersNestingLevelMap, folders }) => {
  const folder = folders.find((item) => item.id === folderId);
  const nestingLevel = foldersNestingLevelMap[folderId] || 1;

  return (
    <div className="ws-page-manager__folder-option" data-nesting-level={nestingLevel}>
      {folder ? folder.name : label}
    </div>
  );
};

const BulkEditingMoveToDialog = ({
  control,
  fieldName,
  captions,
  folderOptions,
  folders,
  foldersNestingLevelMap,
  onSubmit,
  onCancel,
}) => (
  <Modal
    width={MODAL_WIDTH}
    height="auto"
    overlay="visible"
    resizable={false}
    draggable={false}
    centered
    handleSubmit={onSubmit}
    handleCancel={onCancel}
    onOverlayClick={onCancel}
    onPressEsc={onCancel}
    onPressEnter={onSubmit}
    className="ws-bulk-editing-move-to-dialog"
    floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
  >
    <PanelGroup height="100%">
      <Panel align="middle" theme="gray-100" corners="squared" height="100%">
        <DialogHeader>
          <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
        </DialogHeader>
      </Panel>
      <Panel className="ws-without-border-bottom">
        <Box
          paddingTop="spacing-s"
          paddingLeft="spacing-m"
          paddingRight="spacing-m"
          paddingBottom="spacing-m"
        >
          <ControlGroup title={captions.controlLabel} required>
            <SearchableSelectField
              control={control}
              name={fieldName}
              rules={{
                required: captions.errorMessage,
              }}
              noResultsText={captions.noResultsText}
              placeholder={captions.placeholder}
              options={folderOptions}
              components={{
                Option: (props) => (
                  <FolderOption
                    {...props}
                    folders={folders}
                    foldersNestingLevelMap={foldersNestingLevelMap}
                  />
                ),
              }}
              preventOverflow
            />
          </ControlGroup>
        </Box>
      </Panel>
      <Panel>
        <DialogButtonGroup rounded>
          <DialogButton onClick={onSubmit}>{captions.submit}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton stretch="block" size="large" onClick={onCancel}>
            {captions.cancel}
          </DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

FolderOption.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  label: PropTypes.string,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parentFolderId: PropTypes.string,
    })
  ).isRequired,
  foldersNestingLevelMap: PropTypes.shape({}).isRequired,
};

BulkEditingMoveToDialog.propTypes = {
  control: PropTypes.shape({}).isRequired,
  fieldName: PropTypes.string.isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    controlLabel: PropTypes.string.isRequired,
    noResultsText: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    errorMessage: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
  }).isRequired,
  folderOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parentFolderId: PropTypes.string,
    })
  ).isRequired,
  foldersNestingLevelMap: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default BulkEditingMoveToDialog;
